// Argentine-peso monetary helpers shared by every place that must trust a money figure — the amount
// the user types into the rent field and the authoritative rent/interest the backend returns.
//
// The persisted monetary domain is decimal(10,2): up to ten total digits with two fractional, i.e.
// 0.00 .. 99,999,999.99. Any figure outside that range or with more than two decimals cannot have come
// from that domain, so trusting it would risk silently disagreeing with what actually persisted.

export const MAX_MONETARY_AMOUNT = 99999999.99;

// Number of fractional digits in a number's canonical shortest round-trip representation. Reading the
// decimals off the string (rather than value * 100) is what makes the check EXACT: `1234.56` round-trips
// to "1234.56" (2 decimals) while `1234.560000001` round-trips to "1234.560000001" (9). Scientific
// notation — only produced for magnitudes ≥ 1e21 or < 1e-6, i.e. outside the monetary domain, but a
// malformed value could still be one — is decoded precisely: decimals = mantissaDecimals - exponent.
const fractionalDigitCount = (value: number): number => {
    const text = Math.abs(value).toString();
    const eIndex = text.indexOf('e');
    if (eIndex === -1) {
        const dot = text.indexOf('.');
        return dot === -1 ? 0 : text.length - dot - 1;
    }
    const mantissa = text.slice(0, eIndex);
    const exponent = Number(text.slice(eIndex + 1));
    const mantissaDot = mantissa.indexOf('.');
    const mantissaDecimals = mantissaDot === -1 ? 0 : mantissa.length - mantissaDot - 1;
    return Math.max(0, mantissaDecimals - exponent);
};

// True only when a finite number carries at most two fractional digits, decided EXACTLY from its
// canonical representation — no epsilon tolerance, so genuinely over-precise figures (1.000000001,
// 1234.560000001, 0.290000001, IEEE-754-noisy sums) are rejected and never silently rounded.
export const hasAtMostTwoDecimals = (value: number): boolean =>
    Number.isFinite(value) && fractionalDigitCount(value) <= 2;

// A raw value is a valid decimal(10,2) monetary figure only when it is a real number, non-negative (or
// at least `min`), within range and within precision. Strings, null, booleans, NaN/Infinity, negative,
// oversized and over-precise values are all rejected. `min` lets callers demand a strictly positive
// figure (e.g. a rent) by passing a tiny positive floor.
export const isMonetaryAmount = (value: unknown, { min = 0 }: { min?: number } = {}): value is number =>
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= min &&
    value <= MAX_MONETARY_AMOUNT &&
    hasAtMostTwoDecimals(value);

// Parse an es-AR monetary string into a validated number, or `null` when the input is malformed,
// non-finite, negative, out of the decimal(10,2) range, or over-precise. It tolerates the currency
// symbol and every kind of space the Intl es-AR currency formatter may emit (regular space, NBSP
// U+00A0, narrow NBSP U+202F), treats '.' as the thousands separator and ',' as the decimal mark, and
// requires a well-formed shape so garbage can never be coerced into a spurious 0/number.
export const parseArsAmount = (raw: unknown): number | null => {
    if (typeof raw !== 'string') {
        return null;
    }
    const trimmed = raw.trim();
    if (trimmed === '') {
        return null;
    }
    // Accept exactly the envelope emitted by es-AR formatters: at most one leading peso symbol, with
    // optional regular/NBSP/narrow-NBSP spacing before the amount. Surrounding whitespace is harmless,
    // but currency symbols or whitespace anywhere inside the amount are malformed and must not be
    // globally stripped into a different valid number (for example `1 2`, `1$2`, or `$$1`).
    const envelope = /^(?:\$[ \u00a0\u202f]*)?([^\s$]+)$/;
    const match = envelope.exec(trimmed);
    if (!match) {
        return null;
    }
    const amountText = match[1];
    // Either properly grouped thousands (1.234.567) or a plain run of digits; an optional 1–2 digit
    // decimal part after a comma. Anything else (stray letters, bad grouping, sign, doubled separators)
    // is rejected.
    const grouped = /^\d{1,3}(\.\d{3})*(,\d{1,2})?$/;
    const plain = /^\d+(,\d{1,2})?$/;
    if (!grouped.test(amountText) && !plain.test(amountText)) {
        return null;
    }
    const normalized = amountText.replace(/\./g, '').replace(',', '.');
    const amount = Number(normalized);
    return isMonetaryAmount(amount) ? amount : null;
};

// Add two monetary figures EXACTLY, in integer centavos, so valid decimal(10,2) operands never produce
// IEEE-754 noise (e.g. 500000.01 + 25000.06 = 525000.07, not 525000.0700000001). Both operands must be
// valid decimal(10,2) values and the result must stay in range, otherwise it fails closed with `null`.
// The raw precision checks are not weakened: an over-precise operand is rejected outright.
export const addMonetary = (a: number, b: number): number | null => {
    if (!isMonetaryAmount(a) || !isMonetaryAmount(b)) {
        return null;
    }
    const result = (Math.round(a * 100) + Math.round(b * 100)) / 100;
    return isMonetaryAmount(result) ? result : null;
};

// Subtract two monetary figures EXACTLY in integer centavos, flooring the result at zero (a payment
// correction can never go negative). Both operands must be valid decimal(10,2) values; anything else,
// or an out-of-range result, fails closed with `null`.
export const subtractMonetary = (a: number, b: number): number | null => {
    if (!isMonetaryAmount(a) || !isMonetaryAmount(b)) {
        return null;
    }
    const result = Math.max(0, Math.round(a * 100) - Math.round(b * 100)) / 100;
    return isMonetaryAmount(result) ? result : null;
};
